import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../ui-icons/ui-clear-icon';
import '../ui-search-bar/ui-search-icon';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { convertClassStringToProps } from '../../base/package/util/classMap';
import { dispatchCustomEvent } from '../../base/package/util/event';
import style from './ui-search-bar.css?inline';
import '../ui-spinner/ui-spinner';

const CName = 'ui-search-bar';

export const UISearchBarTheme = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export type UISearchBarTheme = (typeof UISearchBarTheme)[keyof typeof UISearchBarTheme];

export const UISearchBarState = {
  INACTIVE: 'inactive',
  SEARCHING: 'searching',
  ERRORED: 'errored',
} as const;

export type UISearchBarState = (typeof UISearchBarState)[keyof typeof UISearchBarState];

/**
 * @fires ui-search-bar-input
 */
@customElement(CName)
export class UISearchBar extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property({ type: String }) class = '';
  @property({ type: String }) name = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) state: UISearchBarState = UISearchBarState.INACTIVE;
  @property({ type: String }) theme: UISearchBarTheme = UISearchBarTheme.LIGHT;
  @property({ type: String, reflect: true }) value = '';

  @query('input') private readonly _searchInput?: HTMLInputElement;

  render() {
    const classes = {
      ...convertClassStringToProps(this.class),
      [this.theme]: true,
      [this.state]: true,
    };

    return html`
      <div class="${classMap(classes)}">
        ${this._renderIcon()}
        <label for="search-input" class="search-label">${window.$app.$t.get(I18n.base.search)}</label>
        <input
          class="search-input"
          id="search-input"
          type="text"
          name=${this.name}
          placeholder=${this.placeholder}
          value=${this.value}
          @input=${this._handleInput}
          @paste=${this._handleInput}
        />
        ${this._renderClearIcon()}
      </div>
    `;
  }

  private _renderIcon() {
    return this.state === UISearchBarState.SEARCHING
      ? html`<ui-spinner class="spinner-icon"></ui-spinner>`
      : html`<ui-search-icon class="search-icon"></ui-search-icon>`;
  }

  private _renderClearIcon() {
    return this.value.length
      ? html`<ui-clear-icon class="clear-icon" @click=${this._clearInput} tabindex="1"
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._clearInput.bind(this))}
      ></ui-clear-icon>`
      : nothing;
  }

  /**
   * The backend uses this pattern to filter the input string:
   * pattern: '^[a-zA-Z0-9 ]+$'
   */
  private _handleInput(event: InputEvent) {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    if (inputValue.startsWith(' ')) {
      inputElement.value = inputValue.trim();
      return;
    }

    const sanitizedValue = inputValue.replace(/[^a-zA-Z0-9äöüÄÖÜß ]/g, '');

    this.value = sanitizedValue;
    inputElement.value = sanitizedValue;

    dispatchCustomEvent(this, `${CName}-input`, { value: this.value });
  }

  private _clearInput() {
    if (!this._searchInput) {
      window.$app.logger.error('Input element is not found', '_searchInput is undefined');
      return;
    }

    this._searchInput.value = '';
    this.value = '';
    this._searchInput.blur();
    dispatchCustomEvent(this, `${CName}-input`, { value: this.value });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UISearchBar;
  }
}
